import { Fragment, memo, useMemo, useState } from 'react';
import type { CondimentSaleDTO } from '@six-pockets/shared';
import { CheckIcon, SplitIcon } from '@/components/icons';
import { Button, InlineAlert, Modal, Spinner } from '@/components/ui';
import type { SelectedBooking } from '@/context/TablesContext';
import { CustomerPicker } from '@/features/customers/components/CustomerPicker';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import { EMPTY_BILL } from '../calc';
import { sheetLabel, useBillForm } from '../useBillForm';
import { BillBookingsTable } from './BillBookingsTable';
import { BillSplitBar, BillSplitHead } from './BillSplitBar';
import { BillTabsEditor } from './BillTabsEditor';
import { BillTotals } from './BillTotals';
import { CustomerBalanceNote } from './CustomerBalanceNote';
import { PaymentSection } from './PaymentSection';
import styles from '@/styles/components/BillStepsModal/style.scss';

interface BillStepsModalProps {
  /** Ticked ended bookings, from one table or several. Empty for a condiments-only bill. */
  bookings: SelectedBooking[];
  /** Condiment tabs on the bill from the start (Generate Bill on the Condiments page). */
  initialSales?: CondimentSaleDTO[];
  onClose: () => void;
}

const NO_SALES: CondimentSaleDTO[] = [];

/** "Table 1", "Table 1 + Table 2", or "Condiments" for a bill without bookings. */
function billTitle(bookings: SelectedBooking[]): string {
  const names = [...new Set(bookings.map((b) => b.tableName))];
  return names.length ? names.join(' + ') : 'Condiments';
}

/**
 * The bill in two steps. 1 Items: check the bookings and put the condiment tabs on the bill (add missed items or
 * a new tab right here). 2 Payment: the customer, discount, status and payment. Each step fits on a screen.
 */
export const BillStepsModal = memo(function BillStepsModal({ bookings: selected, initialSales = NO_SALES, onClose }: BillStepsModalProps) {
  // The bookings as they were when the bill opened: generating it clears the selection on the cards, and the
  // modal must keep showing them during the success state.
  const [bookings] = useState(selected);
  const form = useBillForm(bookings, initialSales, onClose);
  const { totals, step, setStep, isSplit, sheets, allTotals, parts, active } = form;
  const count = sheets.length;

  // After Split, the first bill with nothing on it (each needs a part of a booking or a tab).
  const emptySheet = allTotals.findIndex((t) => t.blocker === EMPTY_BILL);
  const nothingOnBill = isSplit ? emptySheet >= 0 : bookings.length === 0 && form.selectedSales.length === 0;
  const nextBlocker = form.editingTab
    ? 'Save or cancel the tab you are editing first'
    : nothingOnBill
      ? isSplit
        ? `${sheetLabel(sheets[emptySheet]!, emptySheet)} has nothing on it`
        : 'Add at least one booking or condiment tab'
      : null;

  // The shown bill's part of each booking, for the bookings table's "This bill" column.
  const activeParts = useMemo(
    () => (parts ? Object.fromEntries(bookings.map((b) => [b.id, parts[b.id]?.[active] ?? 0])) : null),
    [parts, bookings, active],
  );

  const confirmLabel = isSplit
    ? `Generate ${count} bills · ${formatINR(form.grandTotal)}`
    : form.status === 'partial' && !totals.partialHint
      ? `Generate bill · ${formatINR(totals.collect)} paid`
      : `Generate bill · ${formatINR(totals.total)}`;
  const subtotal = allTotals.reduce((a, t) => a + t.subtotal, 0);

  // After Split, step 2 walks through the bills: the button shows the next bill not yet seen (after the one shown,
  // wrapping round), and becomes Generate only once every bill has been seen. Staff can still tap the chips.
  const unseen = isSplit ? [...Array(count).keys()].filter((i) => !form.reviewed[i]) : [];
  const nextUnseen = unseen.length ? (unseen.find((i) => i > active) ?? unseen[0]!) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className={styles.title}>
          {billTitle(bookings)}
          {/* The bill number sits beside the title as a badge, so staff can read it off at a glance. */}
          {form.billNumber ? (
            <span className={styles.billNo}>
              {isSplit ? `Bills #${form.billNumber}–${form.billNumber + count - 1}` : `Bill #${form.billNumber}`}
            </span>
          ) : null}
        </span>
      }
      footer={
        step === 'items' ? (
          <>
            <Button variant="secondary" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={!!nextBlocker}
              title={nextBlocker ?? undefined}
              onClick={() => {
                // Split: the payment walk-through starts at the first bill.
                if (isSplit) form.setActive(0);
                setStep('payment');
              }}
            >
              Next: Payment · {formatINR(subtotal)}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="lg" onClick={() => setStep('items')} disabled={form.submitting || form.done}>
              Back
            </Button>
            {nextUnseen !== null && !form.submitting && !form.done ? (
              <Button
                variant="primary"
                size="lg"
                title="Every bill must be checked before they are generated"
                onClick={() => form.setActive(nextUnseen)}
              >
                Next: {sheetLabel(sheets[nextUnseen]!, nextUnseen)} · {formatINR(allTotals[nextUnseen]!.total)}
              </Button>
            ) : (
            /* Generating (spinner) → Bill generated (tick), then the modal closes. The button stays green meanwhile:
               disabled would grey it out, and submit ignores clicks while busy or done. */
            <Button
              variant="primary"
              size="lg"
              disabled={!!form.blocker && !form.submitting && !form.done}
              title={form.blocker ?? undefined}
              aria-busy={form.submitting}
              icon={form.submitting ? <Spinner size={16} /> : form.done ? <CheckIcon size={18} /> : undefined}
              onClick={form.submit}
            >
              <span role="status">
                {form.submitting ? 'Generating…' : form.done ? (isSplit ? 'Bills generated' : 'Bill generated') : confirmLabel}
              </span>
            </Button>
            )}
          </>
        )
      }
    >
      <ol className={styles.steps} aria-label="Bill steps">
        <li className={cx(styles.step, step === 'items' && styles.current)} aria-current={step === 'items' ? 'step' : undefined}>
          <span className={styles.stepNo}>1</span> Items
        </li>
        <li className={styles.divider} aria-hidden />
        <li className={cx(styles.step, step === 'payment' && styles.current)} aria-current={step === 'payment' ? 'step' : undefined}>
          <span className={styles.stepNo}>2</span> Payment
        </li>
      </ol>

      {/* After Split: one chip per bill; the steps below show the bill picked. */}
      {isSplit && <BillSplitBar form={form} />}

      {form.error && <InlineAlert message={form.error} />}

      {step === 'items' ? (
        <>
          {isSplit && <BillSplitHead form={form} />}
          {bookings.length > 0 && (
            <BillBookingsTable
              bookings={bookings}
              parts={activeParts}
              onPartChange={form.setBookingPart}
              action={
                // Doubles: one booking, each team pays its part on its own bill.
                !isSplit ? (
                  <Button size="sm" variant="outline" icon={<SplitIcon />} onClick={form.split} disabled={form.editingTab !== null}>
                    Split bill
                  </Button>
                ) : undefined
              }
            />
          )}
          <BillTabsEditor form={form} />
        </>
      ) : (
        // Keyed by the bill shown, so switching bills starts each field (e.g. the customer search) afresh.
        <Fragment key={active}>
          <CustomerPicker value={form.customer} onChange={form.setCustomer} />
          {/* What the customer still owes on older bills, so staff can mention it (collecting it here comes later). */}
          {form.customer && <CustomerBalanceNote customerId={form.customer.id} />}
          <BillTotals
            bookingsTotal={totals.bookingsTotal}
            pausedMs={totals.pausedMs}
            pauseAmount={totals.pauseAmount}
            condimentsTotal={totals.condimentsTotal}
            subtotal={totals.subtotal}
            total={totals.total}
            discount={form.discount}
            onDiscountChange={form.setDiscount}
            happyHour={form.happyHour}
            hasBookings={form.sheetBookings.length > 0}
          />
          <PaymentSection
            status={form.status}
            onStatusChange={form.setStatus}
            method={form.method}
            onMethodChange={form.setMethod}
            paid={form.paid}
            onPaidChange={form.setPaid}
            upi={form.upi}
            onUpiChange={form.setUpi}
            cash={form.cash}
            onCashChange={form.setCash}
            balanceDue={totals.balanceDue}
            partialHint={totals.partialHint}
            showPayment={totals.showPayment}
            splitLeft={totals.splitLeft}
          />
        </Fragment>
      )}
    </Modal>
  );
});
